import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Camera, Upload, CheckCircle, AlertCircle, RotateCcw, Smartphone, PenTool, Trash2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MobileIDCapturePage = () => {
  const { token } = useParams();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentStep, setCurrentStep] = useState('front'); // front, back, signature, complete
  const [uploading, setUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState({ front: false, back: false, signature: false });
  const [cameraActive, setCameraActive] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  
  // Signature state
  const [isDrawing, setIsDrawing] = useState(false);
  const [signatureData, setSignatureData] = useState(null);
  
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const signatureCanvasRef = useRef(null);
  const streamRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchSession();
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, [token]);

  const fetchSession = async () => {
    try {
      const response = await fetch(`${API_URL}/api/mobile-id/${token}`);
      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('This link is invalid or has expired.');
        } else if (response.status === 410) {
          throw new Error('This session has expired. Please request a new link.');
        }
        throw new Error('Failed to load session');
      }
      const data = await response.json();
      setSession(data);
      
      // Determine initial step based on mode
      if (data.mode === 'signature_only') {
        if (data.has_signature) {
          setCurrentStep('complete');
        } else {
          setCurrentStep('signature');
        }
      } else {
        // ID capture mode
        if (data.has_front && data.requires_back && !data.has_back) {
          setCurrentStep('back');
        } else if (data.has_front && (!data.requires_back || data.has_back)) {
          if (data.has_signature) {
            setCurrentStep('complete');
          } else {
            setCurrentStep('signature');
          }
        }
      }
      
      if (data.has_front) setUploadSuccess(prev => ({ ...prev, front: true }));
      if (data.has_back) setUploadSuccess(prev => ({ ...prev, back: true }));
      if (data.has_signature) setUploadSuccess(prev => ({ ...prev, signature: true }));
      
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { 
          facingMode: 'environment',
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.onloadedmetadata = () => {
          videoRef.current.play().catch(err => console.error('Video play error:', err));
        };
      }
      setCameraActive(true);
    } catch (err) {
      console.error('Camera error:', err);
      setError('Unable to access camera. Please use "Choose from Gallery" instead.');
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setCameraActive(false);
  };

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0);
      const imageData = canvas.toDataURL('image/jpeg', 0.8);
      setCapturedImage(imageData);
      stopCamera();
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setCapturedImage(event.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const retakePhoto = () => {
    setCapturedImage(null);
    startCamera();
  };

  const uploadImage = async () => {
    if (!capturedImage) return;
    
    setUploading(true);
    try {
      const response = await fetch(`${API_URL}/api/mobile-id/${token}/upload`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          side: currentStep,
          image: capturedImage
        })
      });

      if (!response.ok) throw new Error('Upload failed');

      setUploadSuccess(prev => ({ ...prev, [currentStep]: true }));
      setCapturedImage(null);

      // Move to next step
      if (currentStep === 'front') {
        if (session?.requires_back) {
          setCurrentStep('back');
        } else {
          setCurrentStep('signature');
        }
      } else if (currentStep === 'back') {
        setCurrentStep('signature');
      }
      
      await fetchSession();
    } catch (err) {
      setError('Failed to upload image. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  // ============ SIGNATURE FUNCTIONS ============
  
  const initSignatureCanvas = () => {
    const canvas = signatureCanvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Draw signature line
      ctx.strokeStyle = '#cccccc';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(20, canvas.height - 30);
      ctx.lineTo(canvas.width - 20, canvas.height - 30);
      ctx.stroke();
      
      // Draw X
      ctx.fillStyle = '#999999';
      ctx.font = '16px sans-serif';
      ctx.fillText('X', 25, canvas.height - 15);
    }
  };

  useEffect(() => {
    if (currentStep === 'signature') {
      setTimeout(initSignatureCanvas, 100);
    }
  }, [currentStep]);

  const getCoordinates = (e) => {
    const canvas = signatureCanvasRef.current;
    const rect = canvas.getBoundingClientRect();
    
    if (e.touches) {
      return {
        x: e.touches[0].clientX - rect.left,
        y: e.touches[0].clientY - rect.top
      };
    }
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    setIsDrawing(true);
    const canvas = signatureCanvasRef.current;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCoordinates(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const canvas = signatureCanvasRef.current;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCoordinates(e);
    ctx.strokeStyle = '#000066';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
    // Save signature data
    const canvas = signatureCanvasRef.current;
    if (canvas) {
      setSignatureData(canvas.toDataURL('image/png'));
    }
  };

  const clearSignature = () => {
    setSignatureData(null);
    initSignatureCanvas();
  };

  const uploadSignature = async () => {
    if (!signatureData) {
      setError('Please sign before submitting');
      return;
    }
    
    setUploading(true);
    try {
      const response = await fetch(`${API_URL}/api/mobile-id/${token}/signature`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          signature: signatureData
        })
      });

      if (!response.ok) throw new Error('Signature upload failed');

      setUploadSuccess(prev => ({ ...prev, signature: true }));
      setCurrentStep('complete');
      await fetchSession();
    } catch (err) {
      setError('Failed to upload signature. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const skipSignature = () => {
    setCurrentStep('complete');
  };

  // ============ RENDER ============

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (error && !session) {
    return (
      <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center p-4">
        <div className="bg-red-900/50 border border-red-500 rounded-lg p-6 max-w-md text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-2">Error</h2>
          <p className="text-gray-300">{error}</p>
        </div>
      </div>
    );
  }

  // COMPLETE STATE
  const isComplete = currentStep === 'complete' || 
    (session?.mode === 'signature_only' && uploadSuccess.signature) ||
    (session?.mode !== 'signature_only' && uploadSuccess.front && (!session?.requires_back || uploadSuccess.back) && uploadSuccess.signature);
  
  if (isComplete) {
    return (
      <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center p-4">
        <div className="bg-green-900/30 border border-green-500 rounded-lg p-8 max-w-md text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-2" data-testid="complete-heading">All Done!</h2>
          <p className="text-gray-300 mb-4">
            {session?.mode === 'signature_only' 
              ? 'Your signature has been captured successfully.'
              : `Your ID ${uploadSuccess.signature ? 'and signature have' : 'has'} been captured successfully.`
            }
          </p>
          <div className="space-y-2 text-sm text-gray-400">
            {session?.mode !== 'signature_only' && <p>&#10003; Front of ID uploaded</p>}
            {session?.mode !== 'signature_only' && session?.requires_back && <p>&#10003; Back of ID uploaded</p>}
            {uploadSuccess.signature && <p>&#10003; Signature captured</p>}
          </div>
          <p className="mt-6 text-gray-500 text-sm">
            You can close this window now.
          </p>
        </div>
      </div>
    );
  }

  // Progress indicator
  const getStepNumber = () => {
    if (session?.mode === 'signature_only') return 1;
    if (currentStep === 'front') return 1;
    if (currentStep === 'back') return 2;
    if (currentStep === 'signature') return session?.requires_back ? 3 : 2;
    return 0;
  };

  const getTotalSteps = () => {
    if (session?.mode === 'signature_only') return 1;
    let steps = 2; // front + signature
    if (session?.requires_back) steps++;
    return steps;
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <div className="max-w-md mx-auto p-4 space-y-6">
        {/* Header */}
        <div className="text-center pt-4">
          <div className="flex items-center justify-center gap-2 mb-2">
            {session?.mode === 'signature_only' ? (
              <PenTool className="w-6 h-6 text-blue-400" />
            ) : (
              <Smartphone className="w-6 h-6 text-blue-400" />
            )}
            <h1 className="text-xl font-bold" data-testid="page-title">
              {session?.mode === 'signature_only' ? 'Signature Capture' : 'ID Verification'}
            </h1>
          </div>
          {session && (
            <p className="text-gray-400 text-sm" data-testid="session-info">
              {session.mode === 'signature_only' 
                ? `Signature for ${session.client_name}`
                : `${session.id_type_name} for ${session.client_name}`
              }
            </p>
          )}
        </div>

        {/* Progress - hide for signature_only mode */}
        {session?.mode !== 'signature_only' && (
          <div className="flex items-center justify-center gap-2">
            {[...Array(getTotalSteps())].map((_, i) => (
              <div key={i} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                  i + 1 < getStepNumber() ? 'bg-green-600' :
                  i + 1 === getStepNumber() ? 'bg-blue-600' : 'bg-gray-700'
                }`}>
                  {i + 1 < getStepNumber() ? '✓' : i + 1}
                </div>
                {i < getTotalSteps() - 1 && (
                  <div className={`w-8 h-1 ${i + 1 < getStepNumber() ? 'bg-green-600' : 'bg-gray-700'}`} />
                )}
              </div>
            ))}
          </div>
        )}

        {/* Error Alert */}
        {error && (
          <div className="bg-red-900/50 border border-red-500 rounded-lg p-3 flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
            <p className="text-sm">{error}</p>
            <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-300">×</button>
          </div>
        )}

        {/* SIGNATURE STEP */}
        {currentStep === 'signature' && (
          <div className="space-y-4">
            <div className="bg-gray-800 rounded-lg p-4">
              <h2 className="text-lg font-semibold mb-2 flex items-center gap-2">
                <PenTool className="w-5 h-5 text-blue-400" />
                Sign Below
              </h2>
              <p className="text-gray-400 text-sm mb-4">
                Please sign using your finger or stylus
              </p>
              
              <div className="bg-white rounded-lg overflow-hidden touch-none">
                <canvas
                  ref={signatureCanvasRef}
                  width={350}
                  height={150}
                  className="w-full"
                  onMouseDown={startDrawing}
                  onMouseMove={draw}
                  onMouseUp={stopDrawing}
                  onMouseLeave={stopDrawing}
                  onTouchStart={startDrawing}
                  onTouchMove={draw}
                  onTouchEnd={stopDrawing}
                  data-testid="signature-canvas"
                />
              </div>
              
              <div className="flex gap-3 mt-4">
                <button
                  onClick={clearSignature}
                  className="flex-1 py-3 bg-gray-700 hover:bg-gray-600 rounded-lg font-semibold flex items-center justify-center gap-2"
                  data-testid="clear-signature-btn"
                >
                  <Trash2 className="w-4 h-4" />
                  Clear
                </button>
                <button
                  onClick={uploadSignature}
                  disabled={uploading}
                  className="flex-1 py-3 bg-green-600 hover:bg-green-700 disabled:bg-green-800 rounded-lg font-semibold flex items-center justify-center gap-2"
                  data-testid="submit-signature-btn"
                >
                  {uploading ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4" />
                      Submit
                    </>
                  )}
                </button>
              </div>
              
              {session?.mode !== 'signature_only' && (
                <button
                  onClick={skipSignature}
                  className="w-full mt-2 py-2 text-gray-400 hover:text-gray-300 text-sm"
                  data-testid="skip-signature-btn"
                >
                  Skip signature
                </button>
              )}
            </div>
          </div>
        )}

        {/* ID CAPTURE STEPS (front/back) */}
        {(currentStep === 'front' || currentStep === 'back') && (
          <>
            {/* Current Side Info */}
            <div className="bg-gray-800 rounded-lg p-4">
              <h2 className="text-lg font-semibold mb-2">
                {currentStep === 'front' ? 'Front of ID' : 'Back of ID'}
              </h2>
              <p className="text-gray-400 text-sm">
                {currentStep === 'front' 
                  ? 'Capture the front of your ID showing your photo and name'
                  : 'Capture the back of your ID showing the barcode/magnetic stripe'}
              </p>
            </div>

            {/* Camera/Preview Area */}
            <div className="bg-gray-800 rounded-lg overflow-hidden">
              {cameraActive && !capturedImage && (
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className="w-full aspect-[4/3] object-cover"
                />
              )}
              
              {capturedImage && (
                <img 
                  src={capturedImage} 
                  alt="Captured ID" 
                  className="w-full aspect-[4/3] object-cover"
                />
              )}
              
              {!cameraActive && !capturedImage && (
                <div className="w-full aspect-[4/3] bg-gray-700 flex items-center justify-center">
                  <div className="text-center text-gray-400">
                    <Camera className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>Camera preview</p>
                  </div>
                </div>
              )}
              
              <canvas ref={canvasRef} className="hidden" />
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              {!cameraActive && !capturedImage && (
                <>
                  <button
                    onClick={startCamera}
                    className="w-full py-4 bg-blue-600 hover:bg-blue-700 rounded-lg font-semibold flex items-center justify-center gap-2 transition-colors"
                    data-testid="open-camera-btn"
                  >
                    <Camera className="w-5 h-5" />
                    Open Camera
                  </button>
                  
                  <div className="text-center text-gray-500">or</div>
                  
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full py-4 bg-gray-700 hover:bg-gray-600 rounded-lg font-semibold flex items-center justify-center gap-2 transition-colors"
                    data-testid="gallery-btn"
                  >
                    <Upload className="w-5 h-5" />
                    Choose from Gallery
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                </>
              )}

              {cameraActive && (
                <button
                  onClick={capturePhoto}
                  className="w-full py-4 bg-green-600 hover:bg-green-700 rounded-lg font-semibold flex items-center justify-center gap-2 transition-colors"
                  data-testid="take-photo-btn"
                >
                  <Camera className="w-5 h-5" />
                  Take Photo
                </button>
              )}

              {capturedImage && (
                <div className="flex gap-3">
                  <button
                    onClick={retakePhoto}
                    className="flex-1 py-4 bg-gray-700 hover:bg-gray-600 rounded-lg font-semibold flex items-center justify-center gap-2 transition-colors"
                    data-testid="retake-btn"
                  >
                    <RotateCcw className="w-5 h-5" />
                    Retake
                  </button>
                  <button
                    onClick={uploadImage}
                    disabled={uploading}
                    className="flex-1 py-4 bg-green-600 hover:bg-green-700 disabled:bg-green-800 rounded-lg font-semibold flex items-center justify-center gap-2 transition-colors"
                    data-testid="upload-btn"
                  >
                    {uploading ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="w-5 h-5" />
                        Upload
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>

            {/* Tips */}
            <div className="bg-gray-800/50 rounded-lg p-4">
              <h3 className="font-semibold mb-2">Tips for best results:</h3>
              <ul className="text-sm text-gray-400 space-y-1">
                <li>• Place ID on a dark, flat surface</li>
                <li>• Ensure good lighting (avoid glare)</li>
                <li>• Capture the entire ID within the frame</li>
                <li>• Hold phone steady when taking photo</li>
              </ul>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default MobileIDCapturePage;
